/**
 * Audit Logs Page
 * View user action audit logs for compliance and debugging
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Card, Badge, Input } from '../components/ui';
import { JsonViewer } from '../components/ui/JsonViewer';
import { 
  RefreshCw, 
  Search, 
  ChevronDown, 
  ChevronRight,
  Clock,
  User,
  Activity,
  Shield,
  Filter,
  Maximize2,
  X,
} from 'lucide-react';
import { getAuditLogs, getActionStats, type AuditLogEntry, type ActionStats } from '../services/auditLogs.api';
import { cn } from '../utils/cn';

export default function AuditLogs() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [actionStats, setActionStats] = useState<ActionStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('');
  const [selectedUserId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState(50);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Expanded log details
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  
  // Fullscreen modal for viewing log details
  const [fullscreenLog, setFullscreenLog] = useState<AuditLogEntry | null>(null);

  // Fetch logs
  const fetchLogs = useCallback(async (append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setOffset(0);
    }
    setError(null);
    try {
      const currentOffset = append ? offset : 0;
      const [logsData, statsData] = await Promise.all([
        getAuditLogs({
          action: selectedAction || undefined,
          resourceType: selectedResourceType || undefined,
          userId: selectedUserId || undefined,
          search: searchQuery || undefined,
          limit,
          offset: currentOffset,
        }),
        getActionStats(),
      ]);
      
      if (append) {
        setLogs(prev => [...prev, ...logsData.logs]);
      } else {
        setLogs(logsData.logs);
      }
      
      setActionStats(statsData);
      setTotal(logsData.pagination.total);
      setHasMore(logsData.pagination.hasMore);
      setOffset(currentOffset + logsData.logs.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit logs');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [selectedAction, selectedResourceType, selectedUserId, searchQuery, limit, offset]);

  useEffect(() => {
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAction, selectedResourceType, selectedUserId, searchQuery, limit]);

  // Load more logs
  const handleLoadMore = () => {
    fetchLogs(true);
  };

  // Format timestamp
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Baghdad',
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get action badge color
  const getActionBadge = (action: string) => {
    if (action.includes('delete')) {
      return <Badge variant="danger" size="sm">{action}</Badge>;
    }
    if (action.includes('create')) {
      return <Badge variant="success" size="sm">{action}</Badge>;
    }
    if (action.includes('update') || action.includes('change')) {
      return <Badge variant="warning" size="sm">{action}</Badge>;
    }
    if (action.includes('login')) {
      return <Badge variant="info" size="sm">{action}</Badge>;
    }
    return <Badge variant="default" size="sm">{action}</Badge>;
  };

  // Get resource type badge
  const getResourceBadge = (resourceType: string) => {
    const colors: Record<string, string> = {
      zone: 'bg-blue-100 text-blue-700',
      route: 'bg-green-100 text-green-700',
      user: 'bg-purple-100 text-purple-700',
      driver: 'bg-orange-100 text-orange-700',
      merchant: 'bg-pink-100 text-pink-700',
      schedule: 'bg-cyan-100 text-cyan-700',
      pickup_window: 'bg-indigo-100 text-indigo-700',
    };
    
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', colors[resourceType] || 'bg-gray-100 text-gray-700')}>
        {resourceType}
      </span>
    );
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-500 text-sm">
              Track all user actions and system changes
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => fetchLogs(false)}
              loading={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Action Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Actions</option>
              <option value="zone.create">Zone Create</option>
              <option value="zone.update">Zone Update</option>
              <option value="zone.delete">Zone Delete</option>
              <option value="route.assign">Route Assign</option>
              <option value="route.unassign">Route Unassign</option>
              <option value="user.create">User Create</option>
              <option value="user.update">User Update</option>
              <option value="user.delete">User Delete</option>
              <option value="auth.login">Login</option>
              <option value="auth.login_failed">Failed Login</option>
            </select>
          </div>

          {/* Resource Type Filter */}
          <select
            value={selectedResourceType}
            onChange={(e) => setSelectedResourceType(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Resources</option>
            <option value="zone">Zones</option>
            <option value="route">Routes</option>
            <option value="user">Users</option>
            <option value="driver">Drivers</option>
            <option value="merchant">Merchants</option>
            <option value="schedule">Schedules</option>
            <option value="pickup_window">Pickup Windows</option>
          </select>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              size="sm"
            />
          </div>

          {/* Limit */}
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
          >
            <option value="25">25 logs</option>
            <option value="50">50 logs</option>
            <option value="100">100 logs</option>
            <option value="200">200 logs</option>
          </select>

          {/* Action stats */}
          <div className="flex items-center gap-2 ml-auto">
            {actionStats.slice(0, 3).map((stat) => (
              <button
                key={stat.action}
                onClick={() => setSelectedAction(stat.action)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium transition-colors',
                  selectedAction === stat.action
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {stat.action.split('.')[1]}: {stat.count}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200 flex items-center gap-3">
          <Shield className="h-5 w-5 text-red-500" />
          <p className="text-red-800">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchLogs(false)}>
            Retry
          </Button>
        </div>
      )}

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : logs.length === 0 ? (
          <Card className="p-8 text-center">
            <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No audit logs found</h3>
            <p className="text-gray-500 text-sm">
              User actions will appear here as they interact with the system.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              const isExpanded = expandedLogId === log._id;
              
              return (
                <Card
                  key={log._id}
                  padding="none"
                  className={cn(
                    'transition-all',
                    isExpanded && 'ring-2 ring-purple-500'
                  )}
                >
                  {/* Log Header */}
                  <button
                    onClick={() => setExpandedLogId(isExpanded ? null : log._id)}
                    className="w-full p-3 flex items-center gap-3 text-left hover:bg-gray-50"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                    
                    <User className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{log.username}</span>
                        {getActionBadge(log.action)}
                        {getResourceBadge(log.resource_type)}
                      </div>
                      <p className="text-sm text-gray-600 truncate">{log.description}</p>
                    </div>
                    
                    {log.ip_address && (
                      <div className="text-xs text-gray-500 flex-shrink-0">
                        {log.ip_address}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 flex-shrink-0">
                      {formatDate(log.created_at)} {formatTime(log.created_at)}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {/* Fullscreen button */}
                      <div className="flex justify-end mb-3">
                        <button
                          onClick={() => setFullscreenLog(log)}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                        >
                          <Maximize2 className="h-4 w-4" />
                          View Fullscreen
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Changes */}
                        {log.changes && (
                          <div className="lg:col-span-2">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Changes</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {log.changes.before && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Before</p>
                                  <div className="bg-gray-900 p-3 rounded-lg overflow-auto max-h-48">
                                    <JsonViewer data={log.changes.before} colorScheme="red" />
                                  </div>
                                </div>
                              )}
                              {log.changes.after && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">After</p>
                                  <div className="bg-gray-900 p-3 rounded-lg overflow-auto max-h-48">
                                    <JsonViewer data={log.changes.after} colorScheme="green" />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Metadata */}
                        {log.metadata && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Metadata</h4>
                            <div className="bg-gray-900 p-3 rounded-lg overflow-auto max-h-48">
                              <JsonViewer data={log.metadata} colorScheme="blue" />
                            </div>
                          </div>
                        )}

                        {/* Request Info */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Request Info</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Method:</span>
                              <span className="font-mono text-gray-900">{log.request_method || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Path:</span>
                              <span className="font-mono text-gray-900 text-xs truncate max-w-xs">{log.request_path || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">User Agent:</span>
                              <span className="text-gray-900 text-xs truncate max-w-xs">{log.user_agent || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4 text-xs text-gray-500">
                        <span>ID: {log._id}</span>
                        <span>User ID: {log.user_id}</span>
                        {log.resource_id && <span>Resource ID: {log.resource_id}</span>}
                        {log.resource_name && <span>Resource: {log.resource_name}</span>}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
            
            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  loading={isLoadingMore}
                  className="min-w-[200px]"
                >
                  {isLoadingMore ? 'Loading...' : `Load More (${logs.length} of ${total})`}
                </Button>
              </div>
            )}
            
            {/* Pagination Info */}
            {!hasMore && logs.length > 0 && (
              <div className="text-center pt-4 text-sm text-gray-500">
                Showing all {total} logs
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {fullscreenLog && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-purple-500" />
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-gray-900">{fullscreenLog.username}</h2>
                    {getActionBadge(fullscreenLog.action)}
                    {getResourceBadge(fullscreenLog.resource_type)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{fullscreenLog.description}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(fullscreenLog.created_at)} {formatTime(fullscreenLog.created_at)}
                    </span>
                    {fullscreenLog.ip_address && (
                      <span className="text-xs text-gray-500">IP: {fullscreenLog.ip_address}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setFullscreenLog(null)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-6">
                {/* Changes */}
                {fullscreenLog.changes && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Changes</h3>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {fullscreenLog.changes.before && (
                        <div className="flex flex-col min-h-0">
                          <p className="text-xs text-gray-500 mb-2">Before</p>
                          <div className="flex-1 bg-gray-900 p-4 rounded-lg overflow-auto">
                            <JsonViewer data={fullscreenLog.changes.before} colorScheme="red" />
                          </div>
                        </div>
                      )}
                      {fullscreenLog.changes.after && (
                        <div className="flex flex-col min-h-0">
                          <p className="text-xs text-gray-500 mb-2">After</p>
                          <div className="flex-1 bg-gray-900 p-4 rounded-lg overflow-auto">
                            <JsonViewer data={fullscreenLog.changes.after} colorScheme="green" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Raw Request/Response for API calls (Verso, etc.) */}
                {fullscreenLog.metadata && 
                 (fullscreenLog.metadata as { payload?: { rawRequest?: Record<string, unknown> } }).payload?.rawRequest && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Raw Request Body</h3>
                    <div className="bg-gray-900 p-4 rounded-lg overflow-auto max-h-96">
                      <JsonViewer data={(fullscreenLog.metadata as { payload: { rawRequest: Record<string, unknown> } }).payload.rawRequest} colorScheme="blue" />
                    </div>
                  </div>
                )}

                {fullscreenLog.metadata && 
                 (fullscreenLog.metadata as { response?: { rawResponse?: Record<string, unknown> } }).response?.rawResponse && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Raw Response Body</h3>
                    <div className="bg-gray-900 p-4 rounded-lg overflow-auto max-h-96">
                      <JsonViewer data={(fullscreenLog.metadata as { response: { rawResponse: Record<string, unknown> } }).response.rawResponse} colorScheme="green" />
                    </div>
                  </div>
                )}

                {/* Metadata */}
                {fullscreenLog.metadata && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Full Metadata</h3>
                    <div className="bg-gray-900 p-4 rounded-lg overflow-auto max-h-96">
                      <JsonViewer data={fullscreenLog.metadata} colorScheme="blue" />
                    </div>
                  </div>
                )}

                {/* Request Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Request Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Method:</span>
                      <p className="font-mono text-gray-900 mt-1">{fullscreenLog.request_method || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Path:</span>
                      <p className="font-mono text-gray-900 mt-1 break-all">{fullscreenLog.request_path || 'N/A'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">User Agent:</span>
                      <p className="text-gray-900 mt-1 break-all">{fullscreenLog.user_agent || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="text-xs text-gray-500 flex items-center gap-4">
                <span>Log ID: {fullscreenLog._id}</span>
                <span>User ID: {fullscreenLog.user_id}</span>
                {fullscreenLog.resource_id && <span>Resource ID: {fullscreenLog.resource_id}</span>}
              </div>
              <button
                onClick={() => setFullscreenLog(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
